import type { PhotoCrop } from 'types/photo_types';
import type { Dimension, PickRequired, Point, StrictUnion } from 'types/common_types';
import type { CanvasConfiguration, CanvasMovementPayload } from 'domains/canvases';
import type {
  CustomSizeProductCustomOptions,
  ProductCustomOptionsAPIData,
} from 'types/catalog_types';
import type { PendingCollectionAction } from 'domains/photos';
import type { WallPreviewSampleRoom, WallPreviewUserRoom } from 'domains/wall_preview_rooms';
import type { Cart } from 'domains/cart';

/** Type guard used for filtering out null or undefined values from an array */
export function notNull<T>(item: T): item is NonNullable<T> {
  return item != null;
}

/** Validates a point as having a value, which could be 0 */
export function hasPoint(point: Partial<Point>): point is Point {
  return point.x != null && point.y != null;
}

/** Validates the cart data exists in the slice */
export function hasCart(cart: Cart | null): cart is Cart {
  return cart != null && !!cart.id;
}

/**
 * Validate that a crop has all necessary data for building a cart photo
 */
export function validCrop(crop: Partial<PhotoCrop>): crop is Required<PhotoCrop> {
  const requiredCropValues = [
    'filter',
    'rotation',
    'zoom',
    'originalH',
    'originalW',
    'cropX',
    'cropY',
    'cropW',
    'cropH',
  ];

  return requiredCropValues.every((key: keyof PhotoCrop) => notNull(crop[key]));
}

export function validDimension(dimensions: Partial<Dimension>): dimensions is Dimension {
  return notNull(dimensions.height) && notNull(dimensions.width);
}

/** Validate the canvas configuration ids */
export function hasFullyConfiguredCanvas(
  canvas: CanvasConfiguration
): canvas is PickRequired<CanvasConfiguration, 'catalog' | 'subcatalog' | 'product' | 'options'> {
  return !!canvas.catalog && !!canvas.subcatalog && !!canvas.product && !!canvas.options;
}

/** Validate the canvas custom size */
export function validCustomSize(
  canvas: CanvasConfiguration
): canvas is PickRequired<CanvasConfiguration, 'height' | 'width'> {
  return !!canvas.height && !!canvas.width;
}

/** Validate that the custom options on a product has the right properties */
export function validCustomOptions(
  customOptions: ProductCustomOptionsAPIData
): customOptions is CustomSizeProductCustomOptions {
  return (
    customOptions.shortmin != null &&
    customOptions.shortmax != null &&
    customOptions.longmin != null &&
    customOptions.longmax != null &&
    customOptions.increment != null
  );
}

export function hasMovementBounds(
  bounds: CanvasMovementPayload
): bounds is Required<CanvasMovementPayload> {
  return (
    bounds.leftLimit != null &&
    bounds.rightLimit != null &&
    bounds.topLimit != null &&
    bounds.bottomLimit != null
  );
}

export function roomIsUserRoom(
  room: WallPreviewUserRoom | WallPreviewSampleRoom
): room is WallPreviewUserRoom {
  return room.type === 'user';
}

export function validCreateCollectionAction(action: string): action is PendingCollectionAction {
  const createCollectionActions = [
    'create_from_orphans',
    'create_from_existing',
    'create_from_upload',
  ];
  return createCollectionActions.includes(action);
}
